import React, { useEffect, useRef, useState } from "react";
import learningStyles from "../assets/learningStyles.jpeg";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const T = {
  navy: "#A80D2C",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

/**
 * HiddenVAKReport
 * ───────────────
 * Renders the VAK report pixel-perfectly offscreen (matching FINAL_report_of_VAK.html),
 * captures each .vak-page with html2canvas, stitches into a multi-page A4 PDF, then saves it.
 *
 * Props:
 * participantData  – null | { userInfo, scores, dominantStyle, submission_time }
 * onComplete       – () => void
 */
export default function HiddenVAKReport({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false);  // guard against StrictMode double-invoke

  // ── Load html2canvas + jsPDF once ──────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id; s.src = src;
      s.onload = done; s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-vak-v2");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-vak-v2");
  }, []);

  // ── Generate PDF whenever participantData is set ────────────────────────
  useEffect(() => {
    if (!participantData) return;
    // hasRun guards against React StrictMode double-invoke on mount.
    // It is reset in the finally block so that:
    //   • VakSurvey (unmounts/remounts each time) keeps working, AND
    //   • GlobalVAKResponses (permanently mounted, just changes the prop)
    //     can fire again for every new download without getting blocked.
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      await new Promise((r) => setTimeout(r, 1000));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".vak-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`VAK_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("VAK PDF generation failed", err);
      } finally {
        // Reset guard so next download (same instance or remount) works.
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]);

  if (!participantData) return null;

  const { userInfo = {}, scores = {}, dominantStyle = "N/A", submission_time } = participantData;
  const v = scores.Visual || 0;
  const a = scores.Auditory || 0;
  const k = scores.Kinesthetic || 0;
  const total = (v + a + k) || 15;

  const vPct = ((v / total) * 100).toFixed(1) + "%";
  const aPct = ((a / total) * 100).toFixed(1) + "%";
  const kPct = ((k / total) * 100).toFixed(1) + "%";

  const dateStr = submission_time
    ? new Date(submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const resultText = dominantStyle?.includes("/")
    ? `Based on your responses, your primary communication preference spans multiple styles (${dominantStyle}). You can adapt fluidly across visual, auditory, and kinesthetic channels depending on the situation.`
    : dominantStyle === "Visual" ? `Based on your responses, your primary communication preference is Visual. You process information best when you can see it mapped out, illustrated, or demonstrated.`
    : dominantStyle === "Auditory" ? `Based on your responses, your primary communication preference is Auditory. You process information best when ideas are spoken aloud, discussed, or verbally explained.`
    : `Based on your responses, your primary communication preference is Kinesthetic. You process information best through hands-on experience, movement, and direct engagement.`;

  const practicalLearningText = dominantStyle === "Visual"
    ? "For this participant, the strongest learning approach should combine visual structure with active practice. That means charts, written prompts, and diagrams should be paired with hands-on activities and short discussion-based reinforcement, so the dominant style and secondary style both get used in a balanced way."
    : dominantStyle === "Auditory" ? "For this participant, the strongest learning approach should combine verbal discussion with written reinforcement. Spoken walkthroughs, group discussions, and verbal feedback should be paired with concise written summaries so both the dominant and secondary styles are engaged."
    : dominantStyle?.includes("/") ? "For this participant, the learning approach should draw on all three modes in equal measure. Blend visual aids, verbal discussion, and hands-on activities so each session leverages the participant's natural cross-modal flexibility."
    : "For this participant, the strongest learning approach should combine hands-on activities with verbal coaching. Role-plays, simulations, and practical exercises should be paired with guided verbal debriefs and clear visual reference material.";

  const learningStyleDefs = [
    {
      key: "visual",
      color: "#3b5ec0",
      bg: "#f0f4ff",
      emoji: "👁️",
      label: "Visual (V) Learning Preference",
      conceptual: "Visual learners demonstrate a strong preference to rely on mental imagery, structure, and visual cues to organise and retain information, making spatial relationships and visual patterning central to their understanding of complex ideas.",
      cognitiveTitle: "Cognitive Characteristics",
      cognitive: [
        "They prefer information presented in structured formats e.g., flowcharts, matrices, mind maps, dashboards, and annotated slides. Materials such as diagrams, charts, videos, demonstrations, and written instructions are particularly effective because they provide a structured way to interpret information.",
        "Think and recall in terms of \"pictures\" or visual metaphors; often imagine concepts as spatial arrangements or graphic representations.",
        "Respond well to color-coding, highlighting, and visual segmentation, which helps signal importance, sequence or causality.",
      ],
      behaviorTitle: "Behavioral Indicators",
      behavior: [
        "When presented with a document or set of information, they often underline, annotate, or spontaneously sketch diagrams to internalise cause-effect chains or logical structure.",
        "They generally prefer to observe first, either by reading instructions or watching a demonstration, before attempting tasks.",
        "In group discussions around strategy, process, or roles, they may request visual models to clarify relationships, responsibilities, or change pathways.",
        "During presentations or training sessions, they pay close attention to slides and layout, sometimes reorganizing information before engaging with details.",
        "In conversation, they may use expressions like \"I see what you mean,\" \"show me,\" or \"that looks right,\" reflecting how they mentally organize ideas.",
      ],
      eyeCuesLabel: "👁️ Visual Mode (Eye Accessing Cues):",
      eyeCuesBg: "#f8fafc",
      eyeCuesColor: "#3b5ec0",
      eyeCues: [
        { label: "Up right", desc: "Imagining / future visuals" },
        { label: "Up left", desc: "Recalling past visuals" },
        { label: "Straight ahead (unfocused)", desc: "Processing images" },
      ],
    },
    {
      key: "auditory",
      color: "#b93030",
      bg: "#fff5f5",
      emoji: "👂",
      label: "Auditory (A) Learning Preference",
      conceptual: "Auditory learners prefer to process information through spoken language, tone, rhythm, and dialogue. They engage most effectively through listening, discussion, and verbal exchange, with attention to how something is said as well as what is said. They are highly attuned not only to words but also to how those words are delivered.",
      cognitiveTitle: "Cognitive Characteristics",
      cognitive: [
        "Learn best when ideas are explained aloud, discussed in groups, or narrated step-by-step. Clarity often comes through hearing ideas explained and discussed rather than reading them independently.",
        "Often process information by talking it through or mentally replaying what they have heard; spoken instruction tends to be more impactful than written guidance.",
        "Use internal \"self-talk\" or discussion to organise and clarify ideas, sometimes weighing options verbally.",
        "Are sensitive to tone, pace, and emphasis, which shape how they interpret meaning and intent.",
      ],
      behaviorTitle: "Behavioural Indicators",
      behavior: [
        "In meetings, may ask for verbal explanations or want key points talked through before making decisions.",
        "Often refer back to past conversations or specific phrases to maintain clarity and alignment.",
        "Prefer dialogue-based feedback or coaching over purely written notes, and may summarise understanding aloud.",
        "Common phrases such as \"I hear what you're saying,\" \"tell me,\" or \"that sounds right\" reflect this preference.",
        "Engage comfortably in discussions, often thinking aloud, and may disengage if information is presented only visually without explanation.",
      ],
      eyeCuesLabel: "👂 Auditory Mode (Eye Accessing Cues):",
      eyeCuesBg: "#fef2f2",
      eyeCuesColor: "#b93030",
      eyeCues: [
        { label: "Side right", desc: "Imagining future sounds" },
        { label: "Side left", desc: "Recalling past sounds" },
        { label: "Down left", desc: "Internal dialogue (self-talk)" },
      ],
    },
    {
      key: "kinesthetic",
      color: "#d9790a",
      bg: "#fffbeb",
      emoji: "🧠",
      label: "Kinesthetic (K) Learning Preference",
      conceptual: "Kinesthetic learners prefer learning through physical engagement, movement, and direct experience. They understand information best by doing, interacting, and applying ideas in real or simulated contexts.",
      cognitiveTitle: "Cognitive Characteristics",
      cognitive: [
        "Prefer learning by engaging in activities such as role-plays, simulations, hands-on exercises, or trial-and-error experimentation.",
        "In structured settings, they benefit from activities that involve participation—simulations, role play, or applied exercises—rather than extended periods of observation or listening.",
        "Use movement, gesture, and physical interaction (e.g., walking while thinking, using models, or interacting with tools) to support focus and problem-solving.",
        "Often internalize concepts more deeply when they have personally experienced or enacted a scenario, rather than when they have only read or heard about it.",
        "Internalize concepts more deeply through direct experience, often learning by trying, adjusting, and refining in real time.",
      ],
      behaviorTitle: "Behavioral Indicators",
      behavior: [
        "When facing change, process redesign, or new initiatives, they may advocate for pilots, \"test runs,\" or micro-experiments to validate approaches before full-scale rollout.",
        "They gravitate toward interactive formats such as business simulations, scenario-based workshops, or live case studies, where they can act out decisions and experience consequences.",
        "Use gestures, whiteboards, or physical objects to organise and refine ideas during discussions.",
        "Use phrases such as \"let me try,\" \"get a handle on it,\" or \"how does that feel?\" reflecting an action-oriented approach.",
      ],
      eyeCuesLabel: "🧠 Kinesthetic Mode (Eye Accessing Cues):",
      eyeCuesBg: "#fffbeb",
      eyeCuesColor: "#d9790a",
      eyeCues: [
        { label: "Down right", desc: "Accessing feelings/emotions" },
      ],
    },
  ];

  // ── Dominant style keys (handles multi e.g. "Visual/Auditory") ──────────
  const dominantKeys = new Set(
    (dominantStyle || "").split("/").map(s => s.trim().toLowerCase())
  );

  // ── Reorder style definitions so the dominant style(s) come first ───────
  // Page 3 always shows orderedStyleDefs[0] (the dominant style).
  // Page 4 always shows the remaining two (in their original relative order).
  const orderedStyleDefs = [...learningStyleDefs].sort((a, b) => {
    const aDom = dominantKeys.has(a.key) ? 0 : 1;
    const bDom = dominantKeys.has(b.key) ? 0 : 1;
    return aDom - bDom;
  });
  const shortLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);
  const page3Style = orderedStyleDefs[0];
  const page4Styles = orderedStyleDefs.slice(1, 3);
  const page4Label = page4Styles.map(s => shortLabel(s.key)).join(" & ");

  const renderStyleCard = (style) => {
    const isHighlighted = dominantKeys.has(style.key);
    return (
    <div
      key={style.key}
      className="item"
      style={{
        paddingLeft: "1rem",
        marginBottom: "1.5rem",
        borderTop:    isHighlighted ? `1.5px solid ${style.color}` : "1px solid #d9e0e7",
        borderRight:  isHighlighted ? `1.5px solid ${style.color}` : "1px solid #d9e0e7",
        borderBottom: isHighlighted ? `1.5px solid ${style.color}` : "1px solid #d9e0e7",
        borderLeft:   isHighlighted ? `5px solid ${style.color}`   : `4px solid ${style.color}`,
        background:   isHighlighted ? style.bg : "#fff",
        boxShadow:    isHighlighted ? `0 0 0 3px ${style.bg}` : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginBottom: "0.5rem" }}>
        <strong style={{ display: "block", color: style.color, fontSize: "1.15rem" }}>
          {style.label}
        </strong>
        {isHighlighted && (
          <div style={{
            flexShrink: 0,
            background: style.color, color: "#fff",
            fontSize: "10px", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            textTransform: "uppercase", letterSpacing: ".06em",
            whiteSpace: "nowrap",
          }}>
            ★ Your Style
          </div>
        )}
      </div>
      <p style={{ margin: "0 0 0.75rem", color: "#334155", fontSize: "0.92rem", textAlign: "justify" }}>
        <strong>Conceptual Definition:</strong> {style.conceptual}
      </p>
      <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.9rem", color: "#0f2744" }}>{style.cognitiveTitle}</p>
      <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.88rem", color: "#475569" }}>
        {style.cognitive.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
      </ul>
      <p style={{ margin: "0 0 0.35rem", fontWeight: 700, fontSize: "0.9rem", color: "#0f2744" }}>{style.behaviorTitle}</p>
      <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.25rem", fontSize: "0.88rem", color: "#475569" }}>
        {style.behavior.map((pt, i) => <li key={i} style={{ marginBottom: "0.3rem" }}>{pt}</li>)}
      </ul>
      {style.eyeCues && (
        <div style={{ background: style.eyeCuesBg, padding: "0.75rem", borderRadius: "4px", marginTop: "1rem" }}>
          <strong style={{ fontSize: "0.9rem", color: style.eyeCuesColor }}>{style.eyeCuesLabel}</strong>
          <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
            {style.eyeCues.map((cue, i) => (
              <li key={i}><strong>{cue.label}:</strong> {cue.desc}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
    );
  };

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

      <style>{`
        .vak-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color: #1f2937;
          line-height: 1.5;
        }
        .vak-pdf-root .report { max-width: 1100px; margin: 0 auto; padding: 2rem 1rem 4rem; }
        .vak-pdf-root .vak-page {
          background: #ffffff; border: 1px solid #d9e0e7; border-radius: 28px;
          box-shadow: 0 18px 40px rgba(14,39,68,.10); overflow: hidden; margin-bottom: 2rem;
        }
          
        /* Cover */
        .vak-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .vak-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .vak-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index: 2; }
        .vak-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }
        
        .vak-pdf-root .logo-panel {
          position: absolute; 
          top: 0; 
          right: 0; 
          width: 500px; 
          height: 250px; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          z-index: 1;
        }        
        .vak-pdf-root .logo-panel-inner {
          display: flex; 
          flex-direction: column; 
          align-items: center; 
          position: relative; 
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .vak-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .vak-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .vak-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; text-align:justify; }
        .vak-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .vak-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .vak-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .vak-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .vak-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .vak-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .vak-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem; }

        /* Inner pages */
        .vak-pdf-root .page-inner { padding: 2.5rem; }
        .vak-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:2rem; }
        .vak-pdf-root .section-kicker { color:#c62828; font-size:0.8rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .vak-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }
        .vak-pdf-root .summary-grid { display:grid; grid-template-columns:1fr; gap:1rem; }
        .vak-pdf-root .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #d9e0e7; border-radius:20px; padding:1.5rem; }
        .vak-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#0f2744; }
        .vak-pdf-root .lead { color:#334155; font-size:1rem; margin:0; text-align:justify; }
        .vak-pdf-root .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
        .vak-pdf-root .pill.blue { background:#fef2f2; color:#c62828; }
        /* Bars */
        .vak-pdf-root .bars { display:grid; gap:1.5rem; margin-top:1rem; }
        .vak-pdf-root .bar-row { display:grid; grid-template-columns:120px 1fr 60px; gap:1rem; align-items:center; }
        .vak-pdf-root .bar-label { font-size:1rem; font-weight:700; color:#0f2744; }
        .vak-pdf-root .track { height:24px; background:#e9eef3; border-radius:999px; overflow:hidden; position:relative; }
        .vak-pdf-root .fill { height:100%; border-radius:999px; position:relative; }
        .vak-pdf-root .fill.vis { background:linear-gradient(90deg,#1e3a8a,#3b82f6); }
        .vak-pdf-root .fill.aud { background:linear-gradient(90deg,#7f1d1d,#ef4444); }
        .vak-pdf-root .fill.kin { background:linear-gradient(90deg,#b45309,#f59e0b); }
        .vak-pdf-root .score-box { text-align:right; font-variant-numeric:tabular-nums; }
        .vak-pdf-root .score-box strong { display:block; color:#0f2744; font-size:1.1rem; }
        /* Callout & List */
        .vak-pdf-root .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid #d9e0e7; border-radius:18px; padding:1.25rem 1.5rem; color:#334155; text-align:justify; }
        .vak-pdf-root .list { display:grid; gap:.9rem; }
        .vak-pdf-root .item { border:1px solid #d9e0e7; border-radius:16px; padding:1.25rem; background:#fff; }
        .vak-pdf-root .item p { margin:0.5rem 0 0; color:#475569; font-size:0.9rem; text-align:justify; }

        /* Back cover */
        .vak-pdf-root .back-cover { min-height:1556px; color:white; }
        .vak-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .vak-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .vak-pdf-root .closing p  { text-align: justify; max-width: 100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .vak-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .vak-pdf-root .contact { background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .vak-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .vak-pdf-root .contact strong { font-size:0.92rem; }
        .vak-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .vak-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="vak-pdf-root">
        <main className="report">

          {/* PAGE 1 — COVER */}
          <section className="vak-page cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
                flexDirection: "column",
              }}
            >
              {/* HTML2CANVAS FIX: SVG Paths with perfectly rounded bottom-left corner */}
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  {/* Drop shadows using paths to match the curved corner */}
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  {/* Main White Path: Q designates the smooth Quadratic Bezier curve at the tip */}
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: 'relative', zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>VAK Learning Style Assessment</h2>
                    <p>
                      This comprehensive assessment report presents an analysis of your communication and learning preferences using the VAK framework, helping identify how you most effectively understand information, engage with others, and develop skills.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta"><span>Participant</span><strong>{userInfo.name || "—"}</strong></div>
                    <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                    <div className="meta"><span>Primary Style</span><strong>{dominantStyle}</strong></div>
                  </div>

                  <div className="cover-badges">
                    {["Assessment Summary", "VAK Distribution", "Style Implications"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: 'relative', zIndex: 2 }}>
                  <div>Based on the Visual / Auditory / Kinesthetic (VAK) Preference Survey framework.</div>
                  {/* <div>Max 15 points distributed across all three modes.</div> */}
                </div>
              </div>
            </div>
          </section>

          {/* PAGE 2 — ASSESSMENT SUMMARY */}
          <section className="vak-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
                <div className="pill blue">Dominant Mode: {dominantStyle}</div>
              </div>

              <div className="summary-grid">
                <div className="card">
                  <h3>Understanding Your Mental Map</h3>
                  <p className="lead">While each individual has one primary mode in which they prefer to communicate, we all have the ability to move from one mode to another depending on the situation—just as a right-handed person can still use their left hand, even if the right is more comfortable.</p>
                  <p className="lead" style={{ marginTop: "1rem" }}>The survey you just completed was designed to identify your predominant perpetual preference. These scores reveal your dominant mental map, providing valuable insights into how you best receive information and how you can build deeper rapport with others by matching their communication styles.</p>
                </div>

                <div className="card">
                  <h3>Why Identifying Your Style Matters</h3>
                  <p className="lead">Research highlights that when individuals are aware of their own learning styles, they can apply appropriate techniques to make absorbing new information significantly faster and more efficient. Knowing your preferred style is not only helpful in classroom or training settings, but it also plays a crucial role in everyday life by increasing problem-solving, analyzing, and overall analytical skills.</p>
                </div>

                <div className="card">
                  <h3>The Three Representational Systems</h3>
                  <p className="lead" style={{ marginBottom: "1rem" }}>Below is an elaborated breakdown of the VAK learning styles, outlining the unique characteristics, preferences, and language associated with each style.</p>
                  <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                    <img
                      src={learningStyles}
                      alt="VAK Learning Styles Chart"
                      crossOrigin="anonymous"
                      style={{ maxWidth: "100%", height: "auto", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", maxHeight: "300px", objectFit: "contain" }}
                    />
                  </div>
                </div>

                <div className="card">
                  <h3>Your VAK Distribution</h3>
                  <div className="bars">
                    <div className="bar-row">
                      <div className="bar-label">Visual (V)</div>
                      <div className="track"><div className="fill vis" style={{ width: vPct }}></div></div>
                      <div className="score-box"><strong>{v}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Auditory (A)</div>
                      <div className="track"><div className="fill aud" style={{ width: aPct }}></div></div>
                      <div className="score-box"><strong>{a}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Kinesthetic (K)</div>
                      <div className="track"><div className="fill kin" style={{ width: kPct }}></div></div>
                      <div className="score-box"><strong>{k}</strong></div>
                    </div>
                  </div>
                  <p className="small" style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    Scores represent the number of choices matching each style (Total: {total} questions).
                  </p>
                </div>

                <div className="callout" style={{ marginTop: "0.75rem" }}>
                  <strong>Your Result:</strong> {resultText}
                </div>
              </div>
            </div>
          </section>

          {/* PAGE 3 — VAK STYLE DEFINITIONS (VISUAL & AUDITORY) */}
          <section className="vak-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Learning Style Definitions</h2>
                </div>
                <div className="pill blue" style={{ background: page3Style.bg, color: page3Style.color }}>{shortLabel(page3Style.key)}</div>
              </div>
              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <h3>VAK Parameters: Conceptual Definitions Across Roles and Levels</h3>
                <p className="lead">
                  The VAK (Visual–Auditory–Kinesthetic) framework describes how individuals tend to perceive, process, and internalize information most effectively. Rather than assuming a single method suits everyone, it emphasizes that people often favor different sensory channels.
                </p>
              </div>
              <div className="list">
                {[page3Style].map(renderStyleCard)}
              </div>
             
            </div>
          </section>

          {/* PAGE 4 — VAK STYLE DEFINITIONS (KINESTHETIC) */}
          <section className="vak-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Learning Style Definitions</h2>
                </div>
                <div className="pill blue" style={{ background: page4Styles[0]?.bg, color: page4Styles[0]?.color }}>{page4Label}</div>
              </div>
              <div className="list">
                {page4Styles.map(renderStyleCard)}
              </div>
            </div>
          </section>

          {/* PAGE 5 — BACK COVER */}
          <section className="vak-page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
                minHeight: "1556px",
              }}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Adaptability is Key</h2>
                    <p>While you naturally lean toward a {dominantStyle} communication style, the most effective leaders learn how to adapt their message to suit the preferred modes of their audience. Use these insights to build stronger connections with your team.</p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Learning Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>{practicalLearningText}</p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>VAK Assessment Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the VAK Learning Style Survey framework.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}